import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import ContainerComponent from '../Container/ContainerComponent';
import MobileNavigation from './MobileNavigation';
import LanguageSwitcher from '@/components/LanguageSwitcher/LanguageSwitcher';
import { type INavigation } from '../../types/index';
import { useConfig } from '@/config';
import { useActiveContent } from '@/content';
import * as m from '@/paraglide/messages.js';
import HomeIcon from '@/assets/icons/home-purple.svg';
import ServicesIcon from '@/assets/icons/services-purple.svg';
import FAQIcon from '@/assets/icons/faq-purple.svg';
import PolicyIcon from '@/assets/icons/policy-purple.svg';
import PhoneIcon from '@/assets/icons/phone-pink.svg';
import EmailIcon from '@/assets/icons/email-pink.svg';
import SettingsIcon from '@/assets/icons/settings-purple.svg';
import SignOutIcon from '@/assets/icons/sign-off-purple.svg';

const Navigation = () => {
  const [isOpen, toggleOpen] = useState(false);
  const [serviceIsOpen, toggleServiceOpen] = useState(false);
  const [mobileIsOpen, toggleMobileOpen] = useState(false);

  const { user, isLoading, loginWithRedirect, logout } = useAuth0();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const { branding, contact, features } = useConfig();
  const { services } = useActiveContent(); // also subscribes to locale changes

  const customerSupportData: INavigation[] = [
    {
      name: contact.phone,
      href: contact.phoneHref,
      src: PhoneIcon,
      subNav: null,
      userMenu: false,
    },
    {
      name: contact.email,
      href: `mailto:${contact.email}`,
      src: EmailIcon,
      subNav: null,
      userMenu: false,
    },
  ];

  const navLinksData: INavigation[] = [
    {
      name: m.nav_dashboard(),
      href: '/dashboard',
      src: HomeIcon,
      subNav: null,
      userMenu: false,
    },
    ...(features.claims
      ? [
          {
            name: m.nav_claims(),
            href: '/claims',
            src: PolicyIcon,
            subNav: null,
            userMenu: false,
          },
        ]
      : []),
    {
      name: m.nav_my_services(),
      href: '#',
      src: ServicesIcon,
      subNav: services.map(s => ({
        name: s.name,
        href: s.href,
        src: s.navIcon,
        subNav: null,
        userMenu: false,
      })),
      userMenu: false,
    },
    ...(features.faq
      ? [
          {
            name: m.nav_faqs(),
            href: '/faq/questions',
            src: FAQIcon,
            subNav: null,
            userMenu: false,
          },
        ]
      : []),
    {
      name: m.nav_settings(),
      href: '/profile',
      src: SettingsIcon,
      subNav: null,
      userMenu: true,
    },
    {
      name: m.nav_sign_out(),
      href: '#',
      src: SignOutIcon,
      subNav: null,
      userMenu: true,
      onClick: () =>
        logout({ logoutParams: { returnTo: window.location.origin } }),
    },
  ];

  return (
    <div className="z-50">
      <nav className="border border-solid border-gray-200">
        <ContainerComponent>
          <div className="flex py-2 md:py-4 justify-between items-center">
            <div className="hidden lg:flex">
              <Link to="/dashboard">
                <img
                  src={branding.logoSrc}
                  alt={branding.logoAlt}
                  width={branding.logoWidth}
                  className="hover:opacity-90"
                />
              </Link>
            </div>

            <div className="ml-auto flex-col hidden lg:flex pb-2">
              {!isLoading && user && (
                <>
                  <div className="h-8 flex items-center justify-end gap-4">
                    {customerSupportData.map(item => (
                      <div
                        className="hover:underline text-brand-accent flex justify-between items-center"
                        key={item.name}
                      >
                        <img className="w-4 h-4" src={item.src} alt="" />
                        <a href={item.href} className="pl-1">
                          {item.name}
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="space-x-4 lg:space-x-12 items-center">
                    {navLinksData
                      .filter(item => !item.userMenu)
                      .map(item =>
                        item.subNav === null ? (
                          <Link
                            key={item.name}
                            to={
                              item.href as
                                | '/dashboard'
                                | '/claims'
                                | '/faq/questions'
                            }
                            className={`px-1 items-center inline-flex font-medium text-center text-sm lg:text-base transition-colors ${
                              pathname.startsWith(item.href)
                                ? 'pt-0.5 border-b-2 border-b-brand-secondary text-brand-secondary'
                                : 'text-gray-700 hover:text-brand-secondary'
                            }`}
                            onClick={() => toggleServiceOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ) : (
                          <div
                            key={item.name}
                            className={`relative px-1 items-center inline-flex font-medium text-sm lg:text-base transition-colors ${
                              pathname.startsWith('/services/')
                                ? 'pt-0.5 border-b-2 border-b-brand-secondary text-brand-secondary'
                                : 'text-gray-700 hover:text-brand-secondary'
                            }`}
                          >
                            <button
                              type="button"
                              id="my-services-button"
                              aria-expanded={serviceIsOpen}
                              aria-haspopup="true"
                              onClick={() => toggleServiceOpen(!serviceIsOpen)}
                              className="cursor-pointer transition-colors hover:text-brand-secondary"
                            >
                              <p className="font-medium">{item.name}</p>
                            </button>
                            {serviceIsOpen && (
                              <div
                                className="absolute z-30 top-full left-0 mt-1 w-52 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="my-services-button"
                                tabIndex={-1}
                              >
                                {item.subNav!.map(subItem => (
                                  <Link
                                    key={subItem.name}
                                    to={
                                      subItem.href as '/services/telemedicine'
                                    }
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-secondary transition-colors"
                                    role="menuitem"
                                    tabIndex={-1}
                                    onClick={() => toggleServiceOpen(false)}
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      )}
                  </div>
                </>
              )}
            </div>

            <div className="hidden lg:flex">
              <div className="ml-4 flex items-center md:ml-6">
                {!isLoading &&
                  (user ? (
                    <div className="relative ml-3">
                      <button
                        type="button"
                        className="relative flex max-w-xs items-center rounded-full bg-brand-secondary text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-secondary cursor-pointer"
                        id="user-menu-button"
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        onClick={() => toggleOpen(!isOpen)}
                      >
                        <span className="sr-only">
                          {m.nav_open_user_menu()}
                        </span>
                        <img
                          className="h-8 w-8 rounded-full"
                          src={user.picture ?? ''}
                          alt="Profile Pic"
                          data-testid="profile-picture"
                        />
                      </button>
                      {isOpen && (
                        <div
                          className="absolute right-0 z-30 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="user-menu-button"
                          tabIndex={-1}
                        >
                          <div className="px-4 py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">
                              {m.nav_choose_your_language()}
                            </span>
                            <LanguageSwitcher />
                          </div>
                          {navLinksData
                            .filter(item => item.userMenu)
                            .map(menuItem =>
                              menuItem.onClick ? (
                                <button
                                  key={menuItem.name}
                                  onClick={menuItem.onClick}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-secondary transition-colors"
                                  role="menuitem"
                                  tabIndex={-1}
                                >
                                  {menuItem.name}
                                </button>
                              ) : (
                                <Link
                                  key={menuItem.name}
                                  to={menuItem.href as '/profile'}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-secondary transition-colors"
                                  role="menuitem"
                                  tabIndex={-1}
                                >
                                  {menuItem.name}
                                </Link>
                              )
                            )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <LanguageSwitcher />
                      <button
                        onClick={() =>
                          loginWithRedirect({
                            appState: { returnTo: '/dashboard' },
                          })
                        }
                        className="text-brand-secondary bg-white border-brand-secondary border-2 hover:bg-brand-primary hover:text-white rounded-md px-12 py-2 mx-5 text-sm font-bold cursor-pointer"
                      >
                        {m.nav_login()}
                      </button>
                      <button
                        onClick={() =>
                          loginWithRedirect({
                            appState: { returnTo: '/dashboard' },
                            authorizationParams: { screen_hint: 'signup' },
                          })
                        }
                        className="text-white bg-brand-secondary hover:bg-brand-primary hover:text-white rounded-md px-3 py-2 text-sm font-bold cursor-pointer"
                      >
                        {m.nav_create_account()}
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            <div className="-mr-2 flex lg:hidden">
              <button
                type="button"
                className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded={mobileIsOpen}
                onClick={() => toggleMobileOpen(!mobileIsOpen)}
              >
                <span className="sr-only">{m.nav_open_main_menu()}</span>
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
              <div className="items-center inline-flex">
                <img
                  src={branding.logoSrc}
                  alt={branding.logoAlt}
                  className="hover:opacity-90 w-2/3 ml-2"
                />
              </div>
            </div>
          </div>
        </ContainerComponent>

        {mobileIsOpen && (
          <div className="lg:hidden" id="mobile-menu">
            <div className="border-t border-gray-700 pb-3 pt-4">
              {!isLoading &&
                (user ? (
                  <>
                    <div className="flex items-center px-7">
                      <img
                        className="h-10 w-10 rounded-full"
                        src={user.picture ?? ''}
                        alt="Profile Pic"
                        data-testid="profile-picture"
                      />
                      <div className="ml-2">
                        <div className="text-sm font-bold leading-none text-black">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 px-5">
                      <LanguageSwitcher />
                    </div>
                    <div className="mt-3 space-y-1 px-2">
                      <MobileNavigation
                        navLinksData={navLinksData}
                        customerSupportData={customerSupportData}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-5 pb-3">
                      <LanguageSwitcher />
                    </div>
                    <button
                      onClick={() =>
                        loginWithRedirect({
                          appState: { returnTo: '/dashboard' },
                        })
                      }
                      className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                    >
                      {m.nav_login()}
                    </button>
                    <button
                      onClick={() =>
                        loginWithRedirect({
                          appState: { returnTo: '/dashboard' },
                          authorizationParams: { screen_hint: 'signup' },
                        })
                      }
                      className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                    >
                      {m.nav_create_account()}
                    </button>
                  </>
                ))}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Navigation;
